package com.schoolconnect.app.repository.analytics

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.SchoolStatisticsResponse
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object SchoolStatisticsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SchoolStatisticsRepository"

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val cachedStatistics = mutableMapOf<String, CachedValue<SchoolStatisticsResponse>>()

    fun peekSchoolStatistics(schoolId: String): SchoolStatisticsResponse? =
        cachedStatistics[cacheKey(schoolId)]?.value

    suspend fun loadSchoolStatistics(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<SchoolStatisticsResponse> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) return Result.failure(IllegalArgumentException("schoolId is required"))
        val key = cacheKey(normalizedSchoolId)
        val cachedValue = if (forceRemote) null else cachedStatistics[key]
        if (forceRemote) {
            scLog("$TAG loadSchoolStatistics forceRemote key=$key")
        }
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolStatistics",
            context = "school=$key",
            cachedValue = cachedValue,
            fetchValue = { KtorApiClient.getSchoolStatistics(normalizedSchoolId) },
            updateCache = { response, fetchedAt ->
                cachedStatistics[key] = CachedValue(response, fetchedAt)
            },
            successDetails = { response ->
                val metrics = response.statistics
                "scope=${response.scope.ifBlank { "<blank>" }} staff=${metrics.staff.total} classes=${metrics.classes.total} subjects=${metrics.subjects.total} students=${metrics.students.total} parents=${metrics.parents.total}"
            }
        )
    }

    fun invalidate(schoolId: String) {
        cachedStatistics.remove(cacheKey(schoolId))
    }

    fun invalidateAll() {
        cachedStatistics.clear()
    }

    private fun cacheKey(schoolId: String): String = tenantCacheKey("school-statistics:${schoolId.trim()}")
}
